/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Toast } from '@adp-arch/bot-semi';
import {
  axiosInstance,
  isApiError,
  type AxiosRequestConfig,
} from '@adp-arch/bot-http';

// Toast display 80px from the top
Toast.config({
  top: 80,
});

interface CustomAxiosConfig {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- keep legacy double-underscore request flag.
  __disableErrorToast?: boolean;
}

const MINIO_HOSTNAME = 'minio';
const LOCAL_STORAGE_PROXY_PREFIX = '/local_storage';
const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const rewriteMinioURL = (value: string) => {
  if (!value.includes('://minio')) {
    return value;
  }

  if (typeof window === 'undefined') {
    return value;
  }

  try {
    const parsedURL = new URL(value);
    if (parsedURL.hostname !== MINIO_HOSTNAME) {
      return value;
    }

    return `${window.location.origin}${LOCAL_STORAGE_PROXY_PREFIX}${parsedURL.pathname}${parsedURL.search}`;
  } catch (error) {
    // Keep the original value when URL parsing fails.
    if (error instanceof Error) {
      return value;
    }
    return value;
  }
};

const normalizeMinioURLs = <T>(value: T): T => {
  if (typeof value === 'string') {
    return rewriteMinioURL(value) as T;
  }

  if (Array.isArray(value)) {
    return value.map(item => normalizeMinioURLs(item)) as T;
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        normalizeMinioURLs(nestedValue),
      ]),
    ) as T;
  }

  return value;
};

/**
 * Business custom axios configuration
 * @param __disableErrorToast default: false
 */
export type BotAPIRequestConfig = AxiosRequestConfig & CustomAxiosConfig;

axiosInstance.interceptors.response.use(
  response => normalizeMinioURLs(response.data),
  error => {
    // business logic
    if (
      isApiError(error) &&
      error.msg &&
      !(error.config as CustomAxiosConfig).__disableErrorToast
    ) {
      Toast.error({
        content: error.msg,
        showClose: false,
      });
    }

    throw error;
  },
);

export { axiosInstance };
